import {
  getStatusIndicator,
  getFriendlyFieldName,
  getTitleOnlineStatusText,
  createCounterRateIndicator,
} from '../../utils/statusUtils';
import styles from '../../styles/components/StatusCard.module.scss';

// Safe JSON stringify to handle circular references
const safeJsonStringify = obj => {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, val) => {
    if (val !== null && typeof val === 'object') {
      if (seen.has(val)) {
        return '[Circular]';
      }
      seen.add(val);
    }
    return val;
  });
};

const StatusCard = ({
  fieldKey,
  value,
  isDeviceOnline = true,
  className = '',
  fullData = null,
  previousData = null,
}) => {
  const statusIndicator = getStatusIndicator(fieldKey, value, fullData);
  const friendlyName = getFriendlyFieldName(fieldKey);
  const isCanImuStatusValue = val =>
    val && typeof val === 'object' && val.type === 'can_imu_status';

  const formatValue = val => {
    if (isCanImuStatusValue(val)) {
      return (
        <>
          <div className={styles.canImuRawList}>{val.rawListText}</div>
          {val.statusLines.map((line, index) => (
            <div
              key={`${line.place}-${line.imuId}-${index}`}
              className={`${styles.canImuStatusLine} ${
                line.isPresent ? styles.successText : styles.errorText
              }`}
            >
              {line.place} ({line.imuId}) {line.isPresent ? '✅' : '❌'}
            </div>
          ))}
        </>
      );
    }

    if (val === null) {
      return 'null';
    }
    if (val === undefined) {
      return 'undefined';
    }
    if (typeof val === 'object') {
      try {
        return safeJsonStringify(val);
      } catch (error) {
        return '[Object - cannot stringify]';
      }
    }
    return String(val);
  };

  // Message rate for the CAN counters
  const rateIndicator = createCounterRateIndicator(fieldKey, fullData, previousData);

  // Use the rate indicator for counter fields, otherwise the status indicator
  const indicator = rateIndicator || statusIndicator;

  return (
    <div className={`${styles.statusCard} ${className}`}>
      <div className={styles.statusCardContent}>
        <div
          className={`${styles.statusCardTitle} ${isDeviceOnline ? styles.online : styles.offline}`}
        >
          {friendlyName}
        </div>
        <div
          className={`${styles.statusCardValue} ${isDeviceOnline ? styles.online : styles.offline}`}
        >
          {formatValue(value)}
        </div>
      </div>

      {indicator && (
        <div className={`${styles.statusCardIndicator} ${styles[indicator.status]}`}>
          {indicator.icon && <indicator.icon className={styles.statusIcon} />}
          {indicator.label && <div className={styles.statusCardLabel}>{indicator.label}</div>}
        </div>
      )}
    </div>
  );
};

const StatusGrid = ({ children, className = '' }) => {
  return <div className={`${styles.statusGrid} ${className}`}>{children}</div>;
};

const OnlineIndicator = ({
  isOnline,
  lastSyncTime,
  inOperation = false,
  batteryPowered = false,
  className = '',
}) => {
  let statusClass = styles.offline;
  if (isOnline) {
    statusClass = inOperation && !batteryPowered ? styles.online : styles.warning;
  }

  const renderSuffix = () => {
    if (!isOnline) {
      return null;
    }
    if (batteryPowered) {
      return ' - In Battery Syncing';
    }
    return inOperation ? ' - In Operation ✅' : ' - Not In Operation';
  };

  return (
    <div className={`${styles.onlineIndicator} ${statusClass} ${className}`}>
      <span className={`${styles.onlineIndicatorText} ${statusClass}`}>
        {getTitleOnlineStatusText(isOnline, lastSyncTime)}
        {renderSuffix()}
      </span>
    </div>
  );
};

StatusCard.Grid = StatusGrid;
StatusCard.OnlineIndicator = OnlineIndicator;

export default StatusCard;
